import type { ComponentProps, MouseEventHandler, ReactNode } from "react";
import { Link, type LinkProps } from "react-router";
import { LearnCard as UiLearnCard, type LearnCardItem } from "../components/domain/LearnCard";
import type { LinkComponentType } from "../types/link";
import { LgCardMeta } from "./LgCardMeta";
import { LgPhrase } from "./LgPhrase";

/** 難易度の値 (beginner など) を、難易度の棒 (LgLevel) が読む言葉にする */
export function learnLevelWord(level: string | null | undefined): string {
  if (level === "beginner") return "初級";
  if (level === "intermediate") return "中級";
  if (level === "advanced") return "上級";
  return "入門";
}

/**
 * コースの難易度の値を言葉にする。beginner、intermediate、advanced の 3 つだけを読み、
 * 値が無い、知らない値のときは null (札に難易度を出さない)。learnLevelWord と違い「入門」を推測で当てない
 */
export function courseLevelWord(level: string | null | undefined): string | null {
  if (level === "beginner") return "初級";
  if (level === "intermediate") return "中級";
  if (level === "advanced") return "上級";
  return null;
}

/**
 * コースの説明を札に出す長さにする。データの説明は 100 字を超える文が多く (中央値 97 字、最長 431 字)、
 * そのままだと札の中で「…」で切れるので、最初の 1 文だけを出す。1 文目で終わる説明は変わらない
 */
export function courseCardLead(description: string | null | undefined): string | undefined {
  const text = description?.trim();
  if (!text) return undefined;
  const end = text.indexOf("。");
  if (end === -1 || end === text.length - 1) return text;
  return text.slice(0, end + 1);
}

export type { LearnCardItem };

/** 外のサイトへのリンク。別のタブで開き、開いた先に元の頁の情報を渡さない。to には https:// の住所を渡す */
function ExternalAnchor({ to, children, className, "aria-label": ariaLabel, onClick }: ComponentProps<LinkComponentType>) {
  return (
    <a href={to} target="_blank" rel="noopener noreferrer" className={className} aria-label={ariaLabel ? `${ariaLabel}（新しいタブで開く）` : undefined} onClick={onClick}>
      {children}
    </a>
  );
}

/**
 * 札ぜんたいを押す釦にする入れ物 (onSelect を渡した札)。リンクと同じ className を受け取り、
 * 押したことを札の側へ返す。開く窓を持つ釦なので aria-haspopup を付ける
 */
function ButtonAnchor({ children, className, onClick, "aria-label": ariaLabel }: ComponentProps<LinkComponentType>) {
  return (
    <button type="button" className={className ? `${className} lgl-card--button` : "lgl-card--button"} aria-haspopup="dialog" aria-label={ariaLabel} onClick={onClick as unknown as MouseEventHandler<HTMLButtonElement>}>
      {children}
    </button>
  );
}

export type LgLearnCardProps = {
  to: string;
  linkProps?: Pick<LinkProps, "onClick" | "prefetch" | "discover">;
  /** card は札ぜんたいがリンク。title は題だけがリンク (札の中に別の釦を置く札) */
  linkMode?: "card" | "title";
  /** title のリンクの読み上げの名前 */
  linkLabel?: string;
  /** 外のサイトの札。to は https:// の住所で、別のタブで開く (用語集の一次情報) */
  external?: boolean;
  /** tile は絵が上 (列に並べる札)、row は絵が左 (横長の札) */
  layout?: "tile" | "row";
  /** 札の絵。img か、絵のないコースの代わりの面 */
  art?: ReactNode;
  /** contain は縦横比がまちまちな図を、窓の中で切らずに収める */
  artFit?: "cover" | "contain";
  /** 絵の窓の縦横比。ogp は 1200 / 630、wide は 16 / 9 */
  artAspect?: "ogp" | "wide";
  tags?: ReactNode;
  title: string;
  /** 題の前に置く小さな印 (用語集の語の印など) */
  titleMark?: ReactNode;
  titleAs?: "h2" | "h3" | "h4";
  text?: string;
  /** 説明を切らずに全文出す */
  textFull?: boolean;
  /** 説明に乗せたときに出す全文 */
  textTitle?: string;
  /** 説明の下の段の並び (トラックの中身)。出さなかった数は itemsMore */
  items?: readonly LearnCardItem[];
  itemsMore?: number;
  /** 出さなかった数の単位 (既定は 個) */
  itemsMoreUnit?: string;
  /** 題と説明のあとに入れる札ごとの中身 */
  extra?: ReactNode;
  /**
   * 難易度の言葉 (入門、初級、中級、上級)。コースの札は必ず出す (courseLevelWord で作る)。
   * 必須にしてあるのは、札を足すときに難易度を忘れないため。コースではない札 (用語、外の資料、操作の手順など)
   * と、値の無いコースは null を渡し、そのときは棒を出さない
   */
  level: string | null;
  /** 難易度の棒のあとに並べる数 (LgMetaItem や span.lg-meta__item) */
  meta?: ReactNode;
  /** 右下の丸い矢印の言葉。done と total があれば進みの輪になる。無ければ矢印を出さない */
  go?: { label: string; done?: number; total?: number };
  /** 丸い矢印の左に並べる札の中の操作 (linkMode が title の札だけ) */
  aside?: ReactNode;
  /** 下の行を出さない (狭い帯の札) */
  bare?: boolean;
  className?: string;
  /** リンクにしない見るだけの札 (HP の見本)。矢印は飾りにする */
  preview?: boolean;
  /**
   * 札ぜんたいを、頁を移らずに窓を開く釦にする。to は使わない (窓の中に行き先の釦を置く)。
   * 渡すと押された釦 (窓を閉じたときにフォーカスを戻す先) を返す
   */
  onSelect?: (trigger: HTMLButtonElement) => void;
};

/**
 * /learn と /luagate の札の出口。形は @noiman/ui の LearnCard が持ち、ここは client にしか無い物
 * (react-router の Link、文節の折り返し LgPhrase、下の行 LgCardMeta) を差し込むだけ。
 * /luagate の外枠の外では、必ず LgCardScope の中に置く
 */
export function LgLearnCard({ title, titleMark, text, level, meta, go, aside, external = false, bare = false, onSelect, ...rest }: LgLearnCardProps) {
  const goProps = go && rest.preview ? { ...go, decorative: true } : go;
  return (
    <UiLearnCard
      {...rest}
      LinkComponent={onSelect ? ButtonAnchor : external ? ExternalAnchor : Link}
      linkProps={onSelect ? { onClick: ((e: { currentTarget: HTMLButtonElement }) => onSelect(e.currentTarget)) as never } : rest.linkProps}
      title={<>{titleMark}<LgPhrase>{title}</LgPhrase></>}
      text={text ? <LgPhrase>{text}</LgPhrase> : undefined}
      footer={
        bare ? null : (
          <LgCardMeta level={level ?? undefined} go={goProps} aside={aside} external={external}>
            {meta}
          </LgCardMeta>
        )
      }
    />
  );
}
